import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { useStore } from '../../store';
import Fab from '../Fab';
import Pagination, { getPageSize } from '../Pagination';
import { RC } from '../../utils/fonts';
import { fmtDateTime } from '../../utils/region';
import { brandOr } from '../../utils/brand';
// The will's intake fills BLANKS ONLY (mergeBlanks) — see src/utils/mergeFill.ts. POA's intake uses
// the other rule in that file, mergeFill, where the document overwrites.
import { mergeBlanks } from '../../utils/mergeFill';
import { propCard, PROP_CARD_BG, PROP_CARD_HOVER, PROP_LIST_WIDTH } from '../../utils/propertyCard';   // the console's green-and-white bubble + the list column's width, shared with Due Diligence / POA / Legal Matters / Property Form

// ── Last Will & Testament — SLF Property's fifth tab ────────────────────────────────────────────
// The form behind the firm's own Word template. Every question here is a blank of that template
// (docs/last-will-blueprint.md §6); the will itself is rendered by the SERVER from the template's
// wording (§7, reproduced 1:1) because the firm's name, address, phone and office mailbox on its
// cover page and signing lines are read from the companies row — they are never in this code.
//
// Two sign-off lines, not Due Diligence's three (the firm's call): "Will Compiled By" is signed by
// whoever compiled it; the content check belongs to the holder of stage 2 of the Last Will sign-off
// role (Admin → Roles). Both must be 'yes' before the will can be printed for signing.

const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 };
const lbl: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13.5, background: 'var(--bg-app)', color: 'var(--text-primary)', boxSizing: 'border-box' };
const btnP: React.CSSProperties = { padding: '8px 18px', fontSize: 13.5, fontWeight: 700, border: 'none', borderRadius: 8, background: 'var(--accent)', color: '#fff', cursor: 'pointer' };
const btnG: React.CSSProperties = { padding: '8px 16px', fontSize: 13.5, fontWeight: 700, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer' };
const hdr: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: 'var(--accent)', marginTop: 4 };
const STATUSES = ['', 'new', 'pending', 'confirmed'];

const emptyPerson = () => ({ title: '', name: '', phone: '', address: '' });
// A personal representative is named in four boxes, as the testator and the spouse are (report
// a1cb9240). `person` holds the four; the row keeps clause 6's own `title` and `name` written beside
// them on every save (withRepPerson), so services/willDocument.ts — and every will saved before the
// boxes existed — keeps printing exactly as it did.
const emptyRepPerson = () => ({ title: '', first: '', middle: '', last: '' });
const emptyRep = () => ({ ...emptyPerson(), person: emptyRepPerson() });
// 'idNo' is the heir's own ID card or passport number. It is kept with the will's answers and shown
// on the form, but it is NOT printed: the firm's template (docs/last-will-blueprint.md §7) words
// clauses 10, 11 and 13 verbatim and has no blank for an heir's number.
const emptyHeir = () => ({ share: '', title: '', name: '', idNo: '', phone: '', addr: { street: '', city: '', province: '', postal: '', country: '' } });
const emptyAsset = () => ({ assetType: '', description: '', bequeathTo: '' });
// The spouse is named in four boxes, as the testator is. Clause 2 still prints ONE name — `spouseName`,
// which services/willDocument.ts reads — so that line is kept as the four put back together (withSpouse).
const emptySpouse = () => ({ title: '', first: '', middle: '', last: '' });
const emptyWill = () => ({
  testator: { title: '', first: '', middle: '', last: '', nationality: '', passportNo: '', addr: { street: '', city: '', province: '', postal: '', country: '' } },
  maritalStatus: '', spouse: emptySpouse(), spouseName: '', hasChildren: '', children: [] as any[], reps: [emptyRep()],
  heirs: [emptyHeir()], assets: [emptyAsset()], mainBeneficiaries: '', secondTier: [] as any[],
  funeralWishes: '', signDate: '', signPlace: '',
});
// The Title box's answers — the testator's, the spouse's and each personal representative's. A title
// read from a document is kept only when it is one of these.
const TITLES = ['Mr.', 'Mrs.', 'Ms.'];

// 'Heir 1 (1st Tier)' — the template's own 'Bequeath To' answers. Built the same way on the server
// (services/willDocument.ts heirLabel), which is what matches an asset to its heir when printing.
const heirLabel = (n: number, tier: 1 | 2): string => `Heir ${n} (${tier === 1 ? '1st' : '2nd'} Tier)`;
// Title · First · Middle · Last, blanks dropped — a name in four boxes as the will prints it.
const nameOf = (p: any): string => [p?.title, p?.first, p?.middle, p?.last]
  .map((x: any) => String(x || '').trim()).filter(Boolean).join(' ');
const fullName = (d: any): string => nameOf(d?.testator);

// ── Heir 1's share is whatever is LEFT of 100% ───────────────────────────────────────────────────
// A will with one heir leaves that heir 100%, and every further heir's percentage comes off heir 1's
// by itself — the box used to open empty, so a lone heir had no share at all and a second heir's
// percentage had to be subtracted from the first by hand. The rule is applied to the 1st-tier list
// and, independently, to the 2nd-tier one.
//
// Heir 1's box is read-only (will_share_auto) and the figure is written into the answers themselves,
// so services/willDocument.ts prints it exactly as the form shows it.
const sharePct = (v: any): number => {
  const n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.]/g, ''));   // '40', '40%', '40.5'
  return Number.isFinite(n) ? n : 0;
};
// 100 less every OTHER row of the tier, never below 0, and cut to two decimals so repeated
// subtraction cannot read 33.340000000000003.
const remainderShare = (rows: any[]): string => {
  const others = rows.slice(1).reduce((s: number, r: any) => s + sharePct(r?.share), 0);
  return String(Math.round(Math.max(0, 100 - others) * 100) / 100);
};
// The answers with heir 1 of each tier holding that remainder — left exactly as they are when it
// already does, so opening a will whose shares add up writes nothing to it.
const withShares = (d: any): any => {
  let n = d;
  for (const key of ['heirs', 'secondTier']) {
    const rows: any[] = Array.isArray(n?.[key]) ? n[key] : [];
    if (!rows.length) continue;
    const v = remainderShare(rows);
    if (String(rows[0]?.share == null ? '' : rows[0].share) === v) continue;
    const l = rows.slice();
    l[0] = { ...(l[0] || emptyHeir()), share: v };
    n = { ...n, [key]: l };
  }
  return n;
};

// True once any answer in a repeating row has been filled in. A row that is still entirely blank is
// where a document's next child / representative / heir / asset goes, so a read document never
// leaves an empty row above the people it found.
const rowFilled = (r: any): boolean => {
  if (r == null) return false;
  if (typeof r === 'string') return r.trim() !== '';
  if (Array.isArray(r)) return r.some(rowFilled);
  if (typeof r === 'object') return Object.values(r).some(rowFilled);
  return false;
};

// Only a status that has a spouse asks for one. While Marital status is still '—' or 'Single' there is
// nobody to name: the boxes are not shown and clause 2's name prints blank.
const SPOUSE_STATUSES = ['Married', 'Divorced', 'Widowed'];
const hasSpouse = (status: any): boolean => SPOUSE_STATUSES.indexOf(String(status || '')) >= 0;
// A name on one line → the four boxes: a leading Mr. / Mrs. / Ms. is the Title (any other word stays
// part of the name), then the first word, the last word, and whatever lies between them as the middle
// name. That one line is what a document's reading gives, and what a will saved before the four boxes
// holds.
const splitName = (v: any): { title: string; first: string; middle: string; last: string } => {
  const s = String(v == null ? '' : v).trim();
  const m = /^(mrs|ms|mr)(?:\.\s*|\s+|$)/i.exec(s);
  const key = m ? m[1].toLowerCase() : '';
  const title = key ? TITLES.find(x => x.replace('.', '').toLowerCase() === key) || '' : '';
  const w = s.slice(title && m ? m[0].length : 0).split(/\s+/).filter(Boolean);
  return { title, first: w[0] || '', middle: w.slice(1, -1).join(' '), last: w.length > 1 ? w[w.length - 1] : '' };
};
// The spouse's four boxes: data.spouse — or, for a will saved with only the one-line name, that name
// split. Reading a will this way writes nothing; the boxes are saved once somebody changes them.
const spouseOf = (d: any) => (rowFilled(d?.spouse) ? { ...emptySpouse(), ...d.spouse } : splitName(d?.spouseName));
// The answers with the spouse's boxes set and clause 2's name made from them again — blank while the
// status has no spouse, so a name nobody can see on the form never prints.
const withSpouse = (d: any, spouse: any): any => ({ ...d, spouse, spouseName: hasSpouse(d?.maritalStatus) ? nameOf(spouse) : '' });

// ── A personal representative's four boxes ───────────────────────────────────────────────────────
// reps[i].person — or, for a will saved before them, that row's old Title + Name put through
// splitName (a leading Mr. / Mrs. / Ms. is the Title; any other word, a 'Dr.', stays part of the
// name). Reading a will this way writes nothing; the boxes are saved once somebody changes them.
const repPerson = (r: any) => (rowFilled(r?.person) ? { ...emptyRepPerson(), ...r.person }
  : splitName([r?.title, r?.name].map((x: any) => String(x || '').trim()).filter(Boolean).join(' ')));
// First · Middle · Last — the name without the Title box.
const bareName = (p: any): string => [p?.first, p?.middle, p?.last].map((x: any) => String(x || '').trim()).filter(Boolean).join(' ');
// The row with its four boxes set and clause 6's two answers made from them again: willDocument.ts
// prints `title` then `name`, so a save always leaves those saying what the boxes say.
const withRepPerson = (r: any, person: any): any => ({ ...r, person, title: String(person?.title || ''), name: bareName(person) });

// ── A personal representative can never be an heir (report a1cb9240) ─────────────────────────────
// Clause 6 appoints the representative; clauses 10 and 13 leave the estate to the heirs, and the
// firm's rule is that those must be different people. The same name in both places is REFUSED — on a
// save and on a document's reading alike — and the message names the person and both places.
//
// Compared on the written name alone: case, spacing and a leading Mr. / Mrs. / Ms. make no
// difference, so 'Mr.  John  Smith' the representative and 'john smith' the heir are one person.
// server/src/services/willDocument.ts holds the same rule and refuses the save; the two must agree,
// or a name this screen lets through would come back refused.
const cmpName = (v: any): string => bareName(splitName(v)).toLowerCase();
// What a row has written in it, Title box included — the honorific is dropped by cmpName itself.
const writtenRep = (r: any): string => (rowFilled(r?.person) ? nameOf(r.person) : [r?.title, r?.name].map((x: any) => String(x || '').trim()).filter(Boolean).join(' '));
const writtenHeir = (h: any): string => [h?.title, h?.name].map((x: any) => String(x || '').trim()).filter(Boolean).join(' ');
const repHeirClash = (d: any): { name: string; rep: number; heir: string } | null => {
  const reps: any[] = Array.isArray(d?.reps) ? d.reps : [];
  for (let i = 0; i < reps.length; i++) {
    const key = cmpName(writtenRep(reps[i]));
    if (!key) continue;
    for (const tier of [1, 2] as (1 | 2)[]) {
      const rows: any[] = Array.isArray(d?.[tier === 1 ? 'heirs' : 'secondTier']) ? d[tier === 1 ? 'heirs' : 'secondTier'] : [];
      const j = rows.findIndex((h: any) => cmpName(writtenHeir(h)) === key);
      if (j >= 0) return { name: writtenRep(reps[i]), rep: i + 1, heir: heirLabel(j + 1, tier) };
    }
  }
  return null;
};

// `readOnly` is an answer the form works out for itself (heir 1's Share %): shown greyed, and the
// title says why it cannot be typed in.
function Txt({ label, value, onChange, ph, type, readOnly, title }: { label: string; value: string; onChange: (v: string) => void; ph?: string; type?: string; readOnly?: boolean; title?: string }) {
  return <div><span style={lbl}>{label}</span><input
    style={{ ...inp, ...(readOnly ? { background: 'var(--c-gray-200)', color: 'var(--text-secondary)', cursor: 'default' } : {}) }}
    type={type || 'text'} placeholder={ph} value={value || ''} readOnly={readOnly} title={title}
    onChange={e => onChange(e.target.value)} /></div>;
}
function Area({ label, value, onChange, ph, rows }: { label: string; value: string; onChange: (v: string) => void; ph?: string; rows?: number }) {
  return <div><span style={lbl}>{label}</span><textarea style={{ ...inp, fontFamily: 'inherit', resize: 'vertical' }} rows={rows || 3} placeholder={ph} value={value || ''} onChange={e => onChange(e.target.value)} /></div>;
}
function Pick({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <div><span style={lbl}>{label}</span>
      <select style={inp} value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">—</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// An address, the five parts blueprint §6 asks for.
function AddrFields({ a, onChange }: { a: any; onChange: (k: string, v: string) => void }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <Txt label={t('will_street')} value={a?.street || ''} onChange={v => onChange('street', v)} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Txt label={t('will_city')} value={a?.city || ''} onChange={v => onChange('city', v)} />
        <Txt label={t('will_province')} value={a?.province || ''} onChange={v => onChange('province', v)} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Txt label={t('will_postal')} value={a?.postal || ''} onChange={v => onChange('postal', v)} />
        <Txt label={t('will_country')} value={a?.country || ''} onChange={v => onChange('country', v)} />
      </div>
    </div>
  );
}

// A repeating row of people — the living children of clause 3: the template's four blanks, title,
// name, phone, one-line address. The personal representatives of clause 6 have their own rows
// (RepRows below), where the name is four boxes instead of one. The heading is left out when the
// card already carries it.
function PersonRows({ heading, rows, onSet, onAdd, onRemove, addLabel, addrLabel }: {
  heading?: string; rows: any[]; onSet: (i: number, patch: any) => void; onAdd: () => void; onRemove: (i: number) => void; addLabel: string; addrLabel: string;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {heading ? <div style={hdr}>{heading}</div> : null}
      {rows.map((p, i) => (
        <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, display: 'grid', gap: 10, background: 'var(--bg-app)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{i + 1})</span>
            <button type="button" onClick={() => onRemove(i)} style={{ ...btnG, padding: '3px 9px', fontSize: 12, color: '#b91c1c' }}>{t('will_remove')}</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr', gap: 10 }}>
            <Txt label={t('will_title')} value={p.title} onChange={v => onSet(i, { title: v })} />
            <Txt label={t('will_name')} value={p.name} onChange={v => onSet(i, { name: v })} />
            <Txt label={t('will_phone')} value={p.phone} onChange={v => onSet(i, { phone: v })} />
          </div>
          <Txt label={addrLabel} value={p.address} onChange={v => onSet(i, { address: v })} />
        </div>
      ))}
      <button type="button" onClick={onAdd} style={{ ...btnG, justifySelf: 'start' }}>+ {addLabel}</button>
    </div>
  );
}

// The personal representatives of clause 6 (report a1cb9240). The same row as a child's, except that
// the one Name box is the four the testator and the spouse have — Title · First · Middle (optional) ·
// Last (reps[i].person) — with the phone and the working or residing address beside them. Their own
// component on purpose: the children above keep PersonRows' single Name box.
// `onName` moves one of the four boxes; `onSet` the phone and the address.
function RepRows({ rows, onName, onSet, onAdd, onRemove, addLabel, addrLabel, titleOpts }: {
  rows: any[]; onName: (i: number, patch: any) => void; onSet: (i: number, patch: any) => void;
  onAdd: () => void; onRemove: (i: number) => void; addLabel: string; addrLabel: string;
  titleOpts: { value: string; label: string }[];
}) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {rows.map((r, i) => {
        const p = repPerson(r);
        return (
          <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, display: 'grid', gap: 10, background: 'var(--bg-app)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t('will_rep_n', { n: i + 1 })}</span>
              <button type="button" onClick={() => onRemove(i)} style={{ ...btnG, padding: '3px 9px', fontSize: 12, color: '#b91c1c' }}>{t('will_remove')}</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 1fr', gap: 10 }}>
              <Pick label={t('will_title')} value={p.title} onChange={v => onName(i, { title: v })} options={titleOpts} />
              <Txt label={t('will_first')} value={p.first} onChange={v => onName(i, { first: v })} />
              <Txt label={t('will_rep_middle')} value={p.middle} onChange={v => onName(i, { middle: v })} />
              <Txt label={t('will_last')} value={p.last} onChange={v => onName(i, { last: v })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
              <Txt label={t('will_phone')} value={r.phone} onChange={v => onSet(i, { phone: v })} />
              <Txt label={addrLabel} value={r.address} onChange={v => onSet(i, { address: v })} />
            </div>
          </div>
        );
      })}
      <button type="button" onClick={onAdd} style={{ ...btnG, justifySelf: 'start' }}>+ {addLabel}</button>
    </div>
  );
}

// ── Smart intake — upload / drag / paste a document and let it fill the blanks ───────────────────
// PER SECTION. One document rarely covers a whole will: a passport answers the testator, a marriage
// or birth certificate the declarations, a letter of instructions the heirs, a list of assets the
// bequests. So every card of the form carries its own small '📎 Fill this section' upload (and takes
// a file dragged, or a screenshot pasted, onto that card) and only that section's answers are
// filled in. The dashed box at the top of the form is the same intake with NO section, for staff who
// do have one file covering everything.
//
// Each section keeps its own busy / filled-in / error state, so two cards can be read at the same
// time and one card's reading never blanks another's. The file itself is never stored; only its
// answers reach the will, which then saves them exactly as it saves typing.
type IntakeState = { busy: boolean; name: string; err: string; done: boolean };
const BLANK_INTAKE: IntakeState = { busy: false, name: '', err: '', done: false };

// What each section's reading is allowed to fill — '' (the box at the top) fills everything.
// Anything the model volunteers outside the section it was given is dropped, so a list of assets
// read on the Heirs card can never move the testator's name.
const SECTION_KEYS: Record<string, string[]> = {
  testator: ['testator'],
  declarations: ['maritalStatus', 'spouseName', 'hasChildren', 'children'],
  reps: ['reps'],
  heirs: ['heirs', 'assets'],
  residue: ['mainBeneficiaries', 'secondTier'],
  funeral: ['funeralWishes', 'signDate'],
};
// An ID card or a passport names ONE person, so it is uploaded on that one heir's own row rather
// than on the Heirs card — and the row, not 'the next blank row', is where the answer goes. The
// intake's key carries the tier and the row position ('heir:1:2' = the 3rd 1st-tier heir); the
// reader itself is told only 'heir', because which tier the row belongs to is the form's business.
const HEIR_SECTION = 'heir';
const heirSlot = (section: string): { tier: 1 | 2; i: number } | null => {
  const m = /^heir:([12]):(\d+)$/.exec(section || '');
  return m ? { tier: m[1] === '1' ? 1 : 2, i: Number(m[2]) } : null;
};
const heirKey = (tier: 1 | 2, i: number): string => `${HEIR_SECTION}:${tier}:${i}`;
const wireSection = (section: string): string => (heirSlot(section) ? HEIR_SECTION : section);

// What a section is CALLED while it is being read — its own card heading(s), so the waiting screen
// and the 'filled in' line name exactly what the operator is looking at.
const SECTION_HEAD: Record<string, string[]> = {
  testator: ['will_sec_testator'],
  declarations: ['will_sec_declarations'],
  reps: ['will_sec_rep'],
  heirs: ['will_sec_heirs', 'will_sec_assets'],
  residue: ['will_sec_residue'],
  funeral: ['will_sec_funeral', 'will_sec_signing'],
};

function useIntake(id: string, onExtract: (section: string, data: any) => void) {
  const { t } = useTranslation();
  const [intake, setIntake] = useState<Record<string, IntakeState>>({});
  // The last reading that was started — what the waiting screen names while anything is busy.
  const [reading, setReading] = useState<{ section: string; name: string }>({ section: '', name: '' });
  const [drag, setDrag] = useState('');
  const [overlayHidden, setOverlayHidden] = useState(false);
  const stateOf = (section: string): IntakeState => intake[section] || BLANK_INTAKE;
  const patch = (section: string, p: Partial<IntakeState>) =>
    setIntake(s => ({ ...s, [section]: { ...(s[section] || BLANK_INTAKE), ...p } }));
  const handleFile = async (section: string, file: File) => {
    const name = file.name || t('will_intake_pasted');
    setOverlayHidden(false);
    setReading({ section, name });
    patch(section, { busy: true, name, err: '', done: false });
    // The reading runs to completion and fills the form even if the waiting screen is closed. The
    // reader is told the SECTION ('heir' for any one heir's row); which row it was is kept here, so
    // two heirs can be read at the same time without one row's state blanking the other's.
    try { const r: any = await api.willExtract(id, file, wireSection(section)); if (r?.data) onExtract(section, r.data); patch(section, { busy: false, done: true }); }
    catch (e: any) { patch(section, { busy: false, err: e?.message || t('will_intake_failed') }); }
  };
  const cancelOverlay = () => { if (window.confirm(t('will_intake_cancel_q'))) setOverlayHidden(true); };
  // A screenshot pasted on a card, or a file dropped on it, belongs to that card: stopPropagation
  // keeps the whole-form handler on the page from reading the very same file a second time.
  const onPaste = (section: string) => (e: React.ClipboardEvent) => {
    const items: any = e.clipboardData?.items || [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.type && it.type.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) { handleFile(section, f); e.preventDefault(); e.stopPropagation(); return; }
      }
    }
  };
  const dropProps = (section: string) => ({
    onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); if (drag !== section) setDrag(section); },
    onDragLeave: (e: React.DragEvent) => { if (e.currentTarget === e.target) setDrag(''); },
    onDrop: (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDrag(''); const f = e.dataTransfer?.files?.[0]; if (f) handleFile(section, f); },
  });
  const busy = Object.keys(intake).some(k => intake[k].busy);
  return { stateOf, busy, reading, overlayHidden, onPaste, dropProps, handleFile, cancelOverlay };
}

// Centred full-screen "analysing…" animation that greys out and blocks the page while the document
// is read. Cancel just closes it (the reading keeps running); the backdrop itself does NOT dismiss.
// When the file came from one card's own upload, the waiting screen says which section is being
// filled in — otherwise it is the whole form.
function IntakeOverlay({ show, name, section, onCancel }: { show: boolean; name: string; section: string; onCancel: () => void }) {
  const { t } = useTranslation();
  const slot = heirSlot(section);
  const sec = (SECTION_HEAD[section] || []).map(k => t(k)).join(' · ');
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <style>{`@keyframes willScan{0%{top:2%;opacity:0}12%{opacity:1}88%{opacity:1}100%{top:82%;opacity:0}}
.will-scanwrap{position:relative;width:100px;height:100px;margin:0 auto;overflow:hidden;border-radius:12px}
.will-scanline{position:absolute;left:6%;right:6%;height:8px;border-radius:6px;background:linear-gradient(90deg,transparent,#48c9ff,transparent);box-shadow:0 0 14px 2px #48c9ff;animation:willScan 1.5s ease-in-out infinite}`}</style>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 14, padding: '30px 36px', textAlign: 'center', maxWidth: 400, boxShadow: '0 14px 44px rgba(0,0,0,.3)' }}>
        <div className="will-scanwrap">
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 100, height: 100, objectFit: 'contain', display: 'block' }} />
          <div className="will-scanline" />
        </div>
        <div style={{ fontSize: 15.5, fontWeight: 700, marginTop: 16, color: 'var(--text-primary)' }}>{t('will_intake_analysing')}</div>
        <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 6 }}>
          {slot ? t('will_intake_heir_reading', { name, heir: heirLabel(slot.i + 1, slot.tier) })
            : sec ? t('will_intake_sec_reading', { name, section: sec }) : t('will_intake_reading', { name })}
        </div>
        <button style={{ ...btnG, marginTop: 20 }} onClick={onCancel}>{t('will_cancel')}</button>
      </div>
    </div>
  );
}

function IntakeBar({ intake, onFile }: { intake: any; onFile: (f: File) => void }) {
  const { t } = useTranslation();
  return (
    <div style={{ border: '2px dashed var(--border)', borderRadius: 10, padding: 14, background: 'var(--bg-surface)', textAlign: 'center', fontSize: 13 }}>
      <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>📎 {t('will_intake_title')}</div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-secondary)' }}>{t('will_intake_hint')}</span>
        <label style={{ ...btnG, cursor: 'pointer', padding: '6px 14px', flexShrink: 0 }}>{t('will_intake_upload')}
          <input type="file" hidden accept="image/*,application/pdf" onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); (e.target as HTMLInputElement).value = ''; }} />
        </label>
      </div>
      {intake.err && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 6 }}>{intake.err}</div>}
      {intake.done && !intake.busy && <div style={{ color: 'var(--accent)', fontSize: 12.5, marginTop: 6 }}>✅ {t('will_intake_done', { name: intake.name })}</div>}
    </div>
  );
}

// The heading row of ONE card: the section's name on the left, its own '📎 Fill this section'
// upload on the right, and under them whatever that one section's last reading has to say.
function SectionHead({ heading, section, st, onFile }: { heading: string; section: string; st: IntakeState; onFile: (f: File) => void }) {
  const { t } = useTranslation();
  const sec = (SECTION_HEAD[section] || []).map(k => t(k)).join(' · ');
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={hdr}>{heading}</div>
        <div style={{ flex: 1 }} />
        {st.busy && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('will_intake_sec_busy')}</span>}
        <label title={t('will_intake_sec_hint', { section: sec })}
          style={{ ...btnG, cursor: 'pointer', padding: '3px 10px', fontSize: 12, flexShrink: 0, opacity: st.busy ? 0.6 : 1 }}>
          📎 {t('will_intake_sec_upload')}
          <input type="file" hidden accept="image/*,application/pdf" onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); (e.target as HTMLInputElement).value = ''; }} />
        </label>
      </div>
      {st.err && <div style={{ color: '#b91c1c', fontSize: 12 }}>{st.err}</div>}
      {st.done && !st.busy && <div style={{ color: 'var(--accent)', fontSize: 12.5 }}>✅ {t('will_intake_sec_done', { name: st.name, section: sec })}</div>}
    </div>
  );
}

// The heading line of ONE heir's row — which heir it is, that row's own '📎 Fill from ID / passport'
// upload, and Remove. An ID card or a passport names one person, so it answers for this row only:
// the title, the name, the ID / passport number and the address land here and nowhere else, and
// only where the row is still blank — a share already typed keeps the last word, as everywhere on
// this form. Used by both tiers, so an heir's row reads the same wherever it is.
function HeirRowHead({ label, st, onFile, onRemove }: {
  label: string; st: IntakeState; onFile: (f: File) => void; onRemove: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{label}</span>
        <div style={{ flex: 1 }} />
        {st.busy && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('will_intake_sec_busy')}</span>}
        <label title={t('will_intake_heir_hint', { heir: label })}
          style={{ ...btnG, cursor: 'pointer', padding: '3px 10px', fontSize: 12, flexShrink: 0, opacity: st.busy ? 0.6 : 1 }}>
          📎 {t('will_intake_heir_upload')}
          <input type="file" hidden accept="image/*,application/pdf" onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); (e.target as HTMLInputElement).value = ''; }} />
        </label>
        <button type="button" onClick={onRemove} style={{ ...btnG, padding: '3px 9px', fontSize: 12, color: '#b91c1c' }}>{t('will_remove')}</button>
      </div>
      {st.err && <div style={{ color: '#b91c1c', fontSize: 12 }}>{st.err}</div>}
      {st.done && !st.busy && <div style={{ color: 'var(--accent)', fontSize: 12.5 }}>✅ {t('will_intake_heir_done', { name: st.name, heir: label })}</div>}
    </div>
  );
}

// ── The two sign-off lines ───────────────────────────────────────────────────────────────────────
function SignoffLines({ id, onChange }: { id: string; onChange?: (allPassed: boolean) => void }) {
  const { t } = useTranslation();
  const [st, setSt] = useState<any>(null);
  const [busy, setBusy] = useState(0);
  const [err, setErr] = useState('');
  const [rejectFor, setRejectFor] = useState(0);
  const [reason, setReason] = useState('');
  const load = async () => { try { const s: any = await api.willSignoff(id); setSt(s); onChange?.(!!s?.allPassed); } catch (e: any) { setErr(e.message); } };
  useEffect(() => { void load(); }, [id]); // eslint-disable-line
  const act = async (stage: number, decision: 'yes' | 'no', why = '') => {
    setBusy(stage); setErr('');
    try { const r: any = await api.willSignoffAct(id, stage, decision, why); setSt(r); onChange?.(!!r?.allPassed); setRejectFor(0); setReason(''); }
    catch (e: any) {
      // The signature is recorded before the notices go out, so a notice that fails (an office
      // mailbox not set, say) must not leave the lines on screen showing the old state: say what
      // went wrong AND re-read them, so the screen matches what was actually saved.
      setErr(e.message); await load();
    } finally { setBusy(0); }
  };
  if (!st) return <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{t('will_loading_signoff')}</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {st.stages.map((s: any) => {
        // `canAct` is the server's own answer, computed with the rule it will enforce — so a button
        // is never drawn that would then come back refused. `blockedMsg` says why when it cannot.
        const myTurn = !!s.canAct && s.decision !== 'yes';
        const done = s.decision === 'yes';
        const no = s.decision === 'no';
        return (
          <div key={s.n} style={{ borderLeft: `3px solid ${done ? '#16a34a' : no ? '#B42318' : 'var(--border)'}`, paddingLeft: 12 }}>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-primary)' }}>{s.n}. {s.label}:</div>
            <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {done && <span style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>✔ {t('will_yes')} — {s.byName}{s.onBehalf ? ` ${t('will_on_behalf')}` : ''}{s.at ? ` · ${fmtDateTime(s.at)}` : ''}</span>}
              {no && <span style={{ fontSize: 13, fontWeight: 700, color: '#B42318' }}>✘ {t('will_changes_requested')} — {s.byName}</span>}
              {!done && !no && <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{s.anyone ? t('will_unsigned_compiler') : t('will_waiting_for', { person: s.person })}</span>}
              {myTurn && rejectFor !== s.n && (
                <>
                  <button onClick={() => act(s.n, 'yes')} disabled={!!busy}
                    style={{ padding: '5px 16px', fontSize: 13, fontWeight: 700, border: 'none', borderRadius: 6, background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                    {busy === s.n ? '…' : (s.role === 'compiler' ? t('will_submit') : t('will_yes'))}
                  </button>
                  <button onClick={() => { setRejectFor(s.n); setReason(''); }} disabled={!!busy}
                    style={{ padding: '5px 16px', fontSize: 13, fontWeight: 700, border: '1px solid #B42318', borderRadius: 6, background: 'var(--bg-surface)', color: '#B42318', cursor: 'pointer' }}>{t('will_no')}</button>
                </>
              )}
              {!myTurn && !done && s.blockedMsg && (
                <span style={{ fontSize: 12, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '3px 9px' }}>
                  <i className="ti ti-lock" style={{ marginRight: 4 }} aria-hidden />{s.blockedMsg}
                </span>
              )}
            </div>
            {no && s.reason && (
              <div style={{ marginTop: 6, padding: '8px 12px', background: '#FEF3F2', border: '1px solid #FECDCA', borderRadius: 6, fontSize: 13, color: '#B42318', whiteSpace: 'pre-wrap' }}>{s.reason}</div>
            )}
            {rejectFor === s.n && (
              <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} autoFocus
                  placeholder={t('will_reason_ph')}
                  style={{ ...inp, fontFamily: 'inherit', resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => act(s.n, 'no', reason)} disabled={!reason.trim() || !!busy}
                    style={{ ...btnP, background: '#B42318', opacity: (!reason.trim() || busy) ? 0.6 : 1 }}>{t('will_send_changes')}</button>
                  <button onClick={() => setRejectFor(0)} style={btnG}>{t('will_cancel')}</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {st.allPassed && <div style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>{t('will_both_signed')}</div>}
      {err && <div style={{ fontSize: 12.5, color: '#b91c1c' }}>{err}</div>}
    </div>
  );
}

// ── The will, as it will print ───────────────────────────────────────────────────────────────────
function WillDocument({ id, approved, rev }: { id: string; approved: boolean; rev: number }) {
  const { t } = useTranslation();
  const [html, setHtml] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setErr('');
    try { const d: any = await api.willDocument(id); setHtml(String(d?.html || '')); }
    catch (e: any) { setErr(e.message); }
  };
  // Re-read it whenever the answers have actually been saved (`rev` is the server's revision), so
  // the will on screen is the will that would print. Delayed a moment so a burst of typing, which
  // saves more than once, redraws it once.
  useEffect(() => { const h = setTimeout(() => { void load(); }, 600); return () => clearTimeout(h); }, [id, approved, rev]); // eslint-disable-line
  // The printable copy comes from the server, which refuses it until both lines are signed — so the
  // gate holds even if this button is reached some other way.
  const doPrint = async () => {
    setBusy(true); setErr('');
    try {
      const doc = await api.willPrintable(id);
      const w = window.open('', '_blank');
      if (!w) { setErr(t('will_popup_blocked')); return; }
      w.document.open(); w.document.write(doc); w.document.close();
      w.focus();
      setTimeout(() => { try { w.print(); } catch { /* the operator can print from the tab */ } }, 400);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={hdr}>{t('will_sec_document')}</div>
        <div style={{ flex: 1 }} />
        {approved
          ? <button onClick={doPrint} disabled={busy} style={{ ...btnP, opacity: busy ? 0.6 : 1 }}><i className="ti ti-printer" style={{ marginRight: 6 }} aria-hidden />{t('will_print')}</button>
          : <span style={{ fontSize: 12, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '4px 10px' }}>
            <i className="ti ti-lock" style={{ marginRight: 4 }} aria-hidden />{t('will_print_locked')}
          </span>}
      </div>
      {err && <div style={{ fontSize: 12.5, color: '#b91c1c' }}>{err}</div>}
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('will_witness_note')}</div>
      {html && <iframe title={t('will_sec_document')} srcDoc={html} style={{ width: '100%', height: 520, border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }} />}
    </div>
  );
}

// ── One will ─────────────────────────────────────────────────────────────────────────────────────
function WillForm({ id, onBack }: { id: string; onBack: () => void }) {
  const { t } = useTranslation();
  const [meta, setMeta] = useState<any>(null);
  const [data, setDataRaw] = useState<any>(emptyWill);
  const [rev, setRev] = useState(0);
  const [save, setSave] = useState<'' | 'saving' | 'saved'>('');
  const [err, setErr] = useState('');
  const [approved, setApproved] = useState(false);
  const loaded = useRef(false);
  const saved = useRef('');   // what the server is known to hold, so opening a will never writes to it
  // The answers the form holds right now — what a document's reading is TRIED against (repHeirClash)
  // before it is applied, a check that cannot be made inside a state update. The reading itself is
  // still applied through the update, so two cards read at once stay in order.
  const live = useRef<any>(data);
  live.current = data;

  // EVERY change to the answers goes through here — typing, adding or removing a row, and a
  // document's reading — so heir 1's share is the remainder of 100% whichever way the list moved,
  // and the autosave below sends that figure. Opening a will whose first share was worked out by
  // hand settles it the same way, so the will on screen is the will that prints.
  const setData = (next: any) => setDataRaw((d: any) => withShares(typeof next === 'function' ? next(d) : next));

  useEffect(() => {
    loaded.current = false;
    api.willGet(id).then((r: any) => {
      const d = { ...emptyWill(), ...(r.data || {}) };
      setMeta(r);
      setData(d);
      setRev(Number(r.data_rev || 0));
      saved.current = JSON.stringify(d);
      loaded.current = true;
    }).catch((e: any) => setErr(e.message));
  }, [id]);

  // A person named both as a personal representative and as an heir (report a1cb9240) — the one
  // thing this form refuses to save. Worked out on every render, so the card says it as soon as the
  // name is typed and the autosave below never sends it.
  const clash = repHeirClash(data);
  const clashMsg = clash ? t('will_rep_is_heir', { name: clash.name, rep: t('will_rep_n', { n: clash.rep }), heir: clash.heir }) : '';

  // Whole-object autosave, with the revision this screen loaded — a save over somebody else's newer
  // one is refused and says so, instead of silently erasing their work. Nothing is sent until an
  // answer actually changes: just reading a will leaves it, and its audit trail, exactly as it was.
  useEffect(() => {
    if (!loaded.current) return;
    const json = JSON.stringify(data);
    if (json === saved.current) return;
    // The clash is never written to the will: the answers stay on screen, the message says who to
    // change, and the save goes the moment one of the two names is different. The server refuses it
    // too (services/willDocument.ts repHeirClash) for any caller that does not come through here.
    if (clashMsg) { setSave(''); setErr(clashMsg); return; }
    setSave('saving');
    const h = setTimeout(() => {
      api.willUpdate(id, { data, baseRev: rev })
        .then((r: any) => { saved.current = json; setRev(Number(r?.rev || 0)); setSave('saved'); setErr(''); })
        .catch((e: any) => { setSave(''); setErr(e.message); });
    }, 700);
    return () => clearTimeout(h);
  }, [data]); // eslint-disable-line

  const set = (patch: any) => setData((d: any) => ({ ...d, ...patch }));
  // A spouse box, or Marital status — which decides whether the spouse is asked for at all. Either one
  // puts clause 2's name together again.
  const setSpouse = (patch: any) => setData((d: any) => withSpouse(d, { ...spouseOf(d), ...patch }));
  const setMarital = (v: string) => setData((d: any) => withSpouse({ ...d, maritalStatus: v }, spouseOf(d)));
  const setT = (patch: any) => setData((d: any) => ({ ...d, testator: { ...d.testator, ...patch } }));
  const setTAddr = (k: string, v: string) => setData((d: any) => ({ ...d, testator: { ...d.testator, addr: { ...d.testator?.addr, [k]: v } } }));
  const setList = (key: string, i: number, patch: any) => setData((d: any) => {
    const l = Array.isArray(d[key]) ? d[key].slice() : [];
    l[i] = { ...l[i], ...patch };
    return { ...d, [key]: l };
  });
  // One of a representative's four name boxes — and clause 6's own `title` / `name` written again
  // from them (withRepPerson), so what prints always matches what the form shows.
  const setRepPerson = (i: number, patch: any) => setData((d: any) => {
    const l = Array.isArray(d.reps) ? d.reps.slice() : [];
    l[i] = withRepPerson(l[i] || emptyRep(), { ...repPerson(l[i]), ...patch });
    return { ...d, reps: l };
  });
  const addTo = (key: string, make: () => any) => setData((d: any) => ({ ...d, [key]: (Array.isArray(d[key]) ? d[key].slice() : []).concat([make()]) }));
  const removeFrom = (key: string, i: number) => setData((d: any) => ({ ...d, [key]: (Array.isArray(d[key]) ? d[key] : []).filter((_: any, j: number) => j !== i) }));
  const setHeirAddr = (i: number, k: string, v: string) => setData((d: any) => {
    const l = Array.isArray(d.heirs) ? d.heirs.slice() : [];
    l[i] = { ...l[i], addr: { ...l[i]?.addr, [k]: v } };
    return { ...d, heirs: l };
  });
  const setSecondAddr = (i: number, k: string, v: string) => setData((d: any) => {
    const l = Array.isArray(d.secondTier) ? d.secondTier.slice() : [];
    l[i] = { ...l[i], addr: { ...l[i]?.addr, [k]: v } };
    return { ...d, secondTier: l };
  });

  // The answers the three dropdowns allow. One list each, so what a document is allowed to fill
  // them with is exactly what a person can choose.
  const titleOpts = TITLES.map(v => ({ value: v, label: v }));
  const maritalOpts = [{ value: 'Single', label: t('will_marital_single') }, { value: 'Married', label: t('will_marital_married') },
    { value: 'Divorced', label: t('will_marital_divorced') }, { value: 'Widowed', label: t('will_marital_widowed') }];
  const yesNoOpts = [{ value: 'Yes', label: t('will_yes') }, { value: 'No', label: t('will_no') }];

  // ── Smart intake ───────────────────────────────────────────────────────────────────────────────
  // What a document says fills BLANKS ONLY: an answer a member of staff has typed is never
  // overwritten by a file, because this form is what the client signs — whoever typed it keeps the
  // last word. The repeating parts grow: a row still blank on the form is used first (a new will
  // opens with one empty representative, heir and asset row) and anything beyond that is appended.
  // The result then goes through the normal autosave, so the sign-off audit and the data_rev lock
  // are untouched.
  //
  // `section` is the card the file was given to (SECTION_KEYS); ONLY that section's answers are
  // applied. '' is the box at the top of the form, which still fills the whole will. A 'heir:<tier>:
  // <row>' key is one heir's own row (heirSlot) — handled first, and only that row is written to.
  //
  // Both merges below are worked out from the answers passed in and RETURN the result rather than
  // writing it, so what a document would do can be checked before any of it is applied.
  //
  // An ID card or a passport given to ONE heir's row: that person goes on THAT row — not on the
  // next blank one — so an heir whose share is already typed is still filled in. Nothing else on
  // the form is touched, whichever keys the reading happens to carry.
  const mergeHeirRow = (d: any, slot: { tier: 1 | 2; i: number }, ex: any): any => {
    const rows: any[] = Array.isArray(ex.heirs) ? ex.heirs.filter((r: any) => r && typeof r === 'object' && rowFilled(r)) : [];
    // An identity document read as the testator's anyway is still this row's person — the file was
    // dropped on the row. Their passport No. is the heir's 'ID / Passport No.'; the testator's own
    // card is never written to from here.
    const tt = ex.testator;
    const asTestator = (tt && typeof tt === 'object' && rowFilled(tt)) ? {
      title: tt.title, phone: '', addr: tt.addr, idNo: tt.passportNo,
      name: [tt.first, tt.middle, tt.last].map((x: any) => String(x || '').trim()).filter(Boolean).join(' '),
    } : null;
    const row = rows[0] || asTestator;
    if (!row) return d;
    const key = slot.tier === 1 ? 'heirs' : 'secondTier';
    const l: any[] = Array.isArray(d[key]) ? d[key].slice() : [];
    if (slot.i < 0 || slot.i >= l.length) return d;   // the row was removed while the file was read
    l[slot.i] = mergeBlanks(l[slot.i] || emptyHeir(), { ...row, share: '' });
    return { ...d, [key]: l };
  };
  const mergeSection = (d: any, section: string, ex: any): any => {
    const keys = SECTION_KEYS[section];
    const ok = (k: string) => !keys || keys.indexOf(k) >= 0;
    const n: any = JSON.parse(JSON.stringify(d || {}));
    // Only an answer a dropdown actually offers can arrive from a document; anything else is
    // dropped rather than written into a will.
    const oneOf = (opts: { value: string }[], v: any): string => {
      const s = String(v == null ? '' : v).trim();
      return opts.some(o => o.value === s) ? s : '';
    };
    if (ok('testator') && ex.testator && typeof ex.testator === 'object') {
      n.testator = mergeBlanks(n.testator || {}, { ...ex.testator, title: oneOf(titleOpts, ex.testator.title) });
    }
    if (ok('maritalStatus')) n.maritalStatus = mergeBlanks(n.maritalStatus || '', oneOf(maritalOpts, ex.maritalStatus));
    if (ok('hasChildren')) n.hasChildren = mergeBlanks(n.hasChildren || '', oneOf(yesNoOpts, ex.hasChildren));
    // The reading still names the spouse on one line. It is split into the four boxes, and only the
    // boxes still blank take it — then clause 2's name is put together again, by the status merged above.
    if (ok('spouseName')) Object.assign(n, withSpouse(n, mergeBlanks(spouseOf(n), splitName(ex.spouseName))));
    if (ok('mainBeneficiaries')) n.mainBeneficiaries = mergeBlanks(n.mainBeneficiaries || '', ex.mainBeneficiaries);
    if (ok('funeralWishes')) n.funeralWishes = mergeBlanks(n.funeralWishes || '', ex.funeralWishes);
    // The date box holds YYYY-MM-DD and nothing else; any other reading is left to a person.
    if (ok('signDate')) {
      const dt = String(ex.signDate == null ? '' : ex.signDate).trim();
      n.signDate = mergeBlanks(n.signDate || '', /^\d{4}-\d{2}-\d{2}$/.test(dt) ? dt : '');
    }

    const rowsOf = (v: any): any[] => (Array.isArray(v) ? v.filter(r => r && typeof r === 'object') : []);
    // Heir 1's Share % is worked out rather than typed (withShares), so it is not an ANSWER: a row
    // holding nothing but that figure is still the blank row a document's first heir goes on.
    const typedRow = (key: string, c: any, j: number): any =>
      (j === 0 && (key === 'heirs' || key === 'secondTier')) ? { ...c, share: '' } : c;
    // Puts the document's rows on the form and says where each one ended up — which is what keeps
    // an asset pointing at its own heir.
    const place = (key: string, rows: any[], make: () => any): number[] => {
      const cur: any[] = Array.isArray(n[key]) ? n[key].slice() : [];
      const at: number[] = [];
      for (const row of rows) {
        let i = cur.findIndex((c: any, j: number) => !rowFilled(typedRow(key, c, j)) && at.indexOf(j) < 0);
        if (i < 0) { cur.push(make()); i = cur.length - 1; }
        cur[i] = mergeBlanks(cur[i] || make(), row);
        at.push(i);
      }
      n[key] = cur;
      return at;
    };
    if (ok('children')) place('children', rowsOf(ex.children), emptyPerson);
    // A representative a document names arrives as one line, which the four boxes then show split
    // (repPerson) — and a row appended for them carries the boxes from the start.
    if (ok('reps')) place('reps', rowsOf(ex.reps), emptyRep);
    const heirAt = ok('heirs') ? place('heirs', rowsOf(ex.heirs), emptyHeir) : [];
    const secondAt = ok('secondTier') ? place('secondTier', rowsOf(ex.secondTier), emptyHeir) : [];
    // 'Heir 2 (1st Tier)' in the document's own numbering is whichever row that heir landed on
    // here — the form may already hold heirs of its own. A bequest that cannot be matched is left
    // with no heir (the form counts those) rather than pointing at the wrong person — which is
    // also what a 2nd-tier bequest read on the Heirs card gets, since that card does not touch
    // the 2nd-tier list.
    const relabel = (v: any): string => {
      const m = /^\s*heir\s+(\d+)\s*\((1st|2nd)\s+tier\)\s*$/i.exec(String(v == null ? '' : v));
      if (!m) return '';
      const tier: 1 | 2 = m[2].toLowerCase() === '1st' ? 1 : 2;
      const at = tier === 1 ? heirAt : secondAt;
      const k = Number(m[1]) - 1;
      return (k >= 0 && k < at.length) ? heirLabel(at[k] + 1, tier) : '';
    };
    if (ok('assets')) place('assets', rowsOf(ex.assets).map((a: any) => ({ ...a, bequeathTo: relabel(a.bequeathTo) })), emptyAsset);
    return n;
  };
  const mergeExtract = (section: string, ex: any) => {
    if (!ex || typeof ex !== 'object') return;
    const slot = heirSlot(section);
    const merge = (d: any): any => (slot ? mergeHeirRow(d, slot, ex) : mergeSection(d, section, ex));
    // The reading goes through the SAME check a save does: one that would name the same person as a
    // personal representative and as an heir is refused WHOLE — the form is left exactly as it was,
    // so a document can never put that clash in quietly. Thrown rather than shown, because useIntake
    // already catches it: the message lands on that card's own line and the card does NOT tick
    // '✅ Filled in' for a reading that was not applied.
    const bad = repHeirClash(merge(live.current));
    if (bad) throw new Error(t('will_rep_is_heir', { name: bad.name, rep: t('will_rep_n', { n: bad.rep }), heir: bad.heir }));
    // Applied through the state update itself, so two cards read at the same time still queue one
    // after the other and neither reading blanks the other's answers.
    setData(merge);
  };
  // Called before the early return below, so the hook runs on every render (React rules-of-hooks).
  const iv = useIntake(id, mergeExtract);

  const heirs: any[] = Array.isArray(data.heirs) ? data.heirs : [];
  const second: any[] = Array.isArray(data.secondTier) ? data.secondTier : [];
  const assets: any[] = Array.isArray(data.assets) ? data.assets : [];
  // Every 'Bequeath To' the template allows: each 1st-tier heir, then each 2nd-tier heir.
  const bequeathOptions = heirs.map((h, i) => ({ value: heirLabel(i + 1, 1), label: `${heirLabel(i + 1, 1)}${h?.name ? ` — ${h.name}` : ''}` }))
    .concat(second.map((h, i) => ({ value: heirLabel(i + 1, 2), label: `${heirLabel(i + 1, 2)}${h?.name ? ` — ${h.name}` : ''}` })));
  const unassigned = assets.filter(a => String(a?.bequeathTo || '').trim() === '').length;
  const spouse = spouseOf(data);

  if (!meta) return <div style={{ padding: 16, color: 'var(--text-tertiary)' }}>{err || t('will_loading')}</div>;

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
      <IntakeOverlay show={iv.busy && !iv.overlayHidden} name={iv.reading.name} section={iv.reading.section} onCancel={iv.cancelOverlay} />
      <div style={{ width: '100%', maxWidth: 892, margin: '0 auto', display: 'grid', gap: 14 }} onPaste={iv.onPaste('')} {...iv.dropProps('')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={onBack} style={btnG}>← {t('will_back')}</button>
          <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{meta.report_no}</span>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{fullName(data) || t('will_untitled')}</span>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{save === 'saving' ? t('will_saving') : save === 'saved' ? t('will_saved') : ''}</span>
        </div>
        {err && <div style={{ padding: '8px 12px', background: '#FEF3F2', border: '1px solid #FECDCA', borderRadius: 8, fontSize: 13, color: '#B42318' }}>{err}</div>}

        {/* One file covering the WHOLE will — every section has its own upload as well */}
        <IntakeBar intake={iv.stateOf('')} onFile={f => iv.handleFile('', f)} />

        {/* Testator */}
        <div style={{ ...card, display: 'grid', gap: 12 }} onPaste={iv.onPaste('testator')} {...iv.dropProps('testator')}>
          <SectionHead heading={t('will_sec_testator')} section="testator" st={iv.stateOf('testator')} onFile={f => iv.handleFile('testator', f)} />
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 1fr', gap: 10 }}>
            <Pick label={t('will_title')} value={data.testator?.title || ''} onChange={v => setT({ title: v })}
              options={titleOpts} />
            <Txt label={t('will_first')} value={data.testator?.first || ''} onChange={v => setT({ first: v })} />
            <Txt label={t('will_middle')} value={data.testator?.middle || ''} onChange={v => setT({ middle: v })} />
            <Txt label={t('will_last')} value={data.testator?.last || ''} onChange={v => setT({ last: v })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Txt label={t('will_nationality')} value={data.testator?.nationality || ''} onChange={v => setT({ nationality: v })} />
            <Txt label={t('will_passport')} value={data.testator?.passportNo || ''} onChange={v => setT({ passportNo: v })} />
          </div>
          <AddrFields a={data.testator?.addr} onChange={setTAddr} />
        </div>

        {/* Preliminary declarations — clauses 2 and 3 */}
        <div style={{ ...card, display: 'grid', gap: 12 }} onPaste={iv.onPaste('declarations')} {...iv.dropProps('declarations')}>
          <SectionHead heading={t('will_sec_declarations')} section="declarations" st={iv.stateOf('declarations')} onFile={f => iv.handleFile('declarations', f)} />
          <Pick label={t('will_marital')} value={data.maritalStatus || ''} onChange={setMarital} options={maritalOpts} />
          {/* The spouse — Married, Divorced or Widowed only; laid out as the testator's name */}
          {hasSpouse(data.maritalStatus) && (
            <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 1fr', gap: 10 }}>
              <Pick label={t('will_spouse_title')} value={spouse.title} onChange={v => setSpouse({ title: v })} options={titleOpts} />
              <Txt label={t('will_spouse_first')} value={spouse.first} onChange={v => setSpouse({ first: v })} />
              <Txt label={t('will_spouse_middle')} value={spouse.middle} onChange={v => setSpouse({ middle: v })} />
              <Txt label={t('will_spouse_last')} value={spouse.last} onChange={v => setSpouse({ last: v })} />
            </div>
          )}
          <Pick label={t('will_has_children')} value={data.hasChildren || ''} onChange={v => set({ hasChildren: v })}
            options={yesNoOpts} />
          <PersonRows heading={t('will_children')} rows={Array.isArray(data.children) ? data.children : []}
            onSet={(i, p) => setList('children', i, p)} onAdd={() => addTo('children', emptyPerson)} onRemove={i => removeFrom('children', i)}
            addLabel={t('will_add_child')} addrLabel={t('will_address')} />
        </div>

        {/* Personal representatives — clause 6 */}
        <div style={{ ...card, display: 'grid', gap: 12 }} onPaste={iv.onPaste('reps')} {...iv.dropProps('reps')}>
          <SectionHead heading={t('will_sec_rep')} section="reps" st={iv.stateOf('reps')} onFile={f => iv.handleFile('reps', f)} />
          <RepRows rows={Array.isArray(data.reps) ? data.reps : []}
            onName={setRepPerson} onSet={(i, p) => setList('reps', i, p)}
            onAdd={() => addTo('reps', emptyRep)} onRemove={i => removeFrom('reps', i)}
            addLabel={t('will_add_rep')} addrLabel={t('will_work_address')} titleOpts={titleOpts} />
          {/* Named here AND among the heirs — the one thing the will refuses to save (report a1cb9240) */}
          {clashMsg && (
            <div style={{ fontSize: 12.5, color: '#B42318', background: '#FEF3F2', border: '1px solid #FECDCA', borderRadius: 6, padding: '6px 10px' }}>
              {clashMsg}
            </div>
          )}
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('will_powers_note')}</div>
        </div>

        {/* Heirs and assets — clause 10 */}
        <div style={{ ...card, display: 'grid', gap: 12 }} onPaste={iv.onPaste('heirs')} {...iv.dropProps('heirs')}>
          <SectionHead heading={t('will_sec_heirs')} section="heirs" st={iv.stateOf('heirs')} onFile={f => iv.handleFile('heirs', f)} />
          {heirs.map((h, i) => (
            <div key={i} onPaste={iv.onPaste(heirKey(1, i))} {...iv.dropProps(heirKey(1, i))}
              style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, display: 'grid', gap: 10, background: 'var(--bg-app)' }}>
              <HeirRowHead label={heirLabel(i + 1, 1)} st={iv.stateOf(heirKey(1, i))}
                onFile={f => iv.handleFile(heirKey(1, i), f)} onRemove={() => removeFrom('heirs', i)} />
              <div style={{ display: 'grid', gridTemplateColumns: '84px 96px 1fr 1fr 1fr', gap: 10 }}>
                {/* Heir 1's share is the remainder of 100%, worked out rather than typed */}
                <Txt label={t('will_share')} value={i === 0 ? remainderShare(heirs) : h.share}
                  onChange={v => setList('heirs', i, { share: v })} readOnly={i === 0} title={i === 0 ? t('will_share_auto') : undefined} />
                <Txt label={t('will_title')} value={h.title} onChange={v => setList('heirs', i, { title: v })} />
                <Txt label={t('will_name')} value={h.name} onChange={v => setList('heirs', i, { name: v })} />
                <Txt label={t('will_id_no')} value={h.idNo} onChange={v => setList('heirs', i, { idNo: v })} />
                <Txt label={t('will_phone')} value={h.phone} onChange={v => setList('heirs', i, { phone: v })} />
              </div>
              <AddrFields a={h.addr} onChange={(k, v) => setHeirAddr(i, k, v)} />
            </div>
          ))}
          <button type="button" onClick={() => addTo('heirs', emptyHeir)} style={{ ...btnG, justifySelf: 'start' }}>+ {t('will_add_heir')}</button>
          {heirs.length > 0 && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('will_share_auto')}</div>}

          <div style={hdr}>{t('will_sec_assets')}</div>
          {assets.map((a, i) => (
            <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, display: 'grid', gap: 10, background: 'var(--bg-app)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{i + 1})</span>
                <button type="button" onClick={() => removeFrom('assets', i)} style={{ ...btnG, padding: '3px 9px', fontSize: 12, color: '#b91c1c' }}>{t('will_remove')}</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Txt label={t('will_asset_type')} value={a.assetType} onChange={v => setList('assets', i, { assetType: v })} ph={t('will_asset_type_ph')} />
                <Pick label={t('will_bequeath_to')} value={a.bequeathTo} onChange={v => setList('assets', i, { bequeathTo: v })} options={bequeathOptions} />
              </div>
              <Area label={t('will_asset_desc')} value={a.description} onChange={v => setList('assets', i, { description: v })} rows={2} />
            </div>
          ))}
          <button type="button" onClick={() => addTo('assets', emptyAsset)} style={{ ...btnG, justifySelf: 'start' }}>+ {t('will_add_asset')}</button>
          {unassigned > 0 && (
            <div style={{ fontSize: 12.5, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '6px 10px' }}>
              {t('will_assets_unassigned', { n: unassigned })}
            </div>
          )}
        </div>

        {/* Residue — clauses 11 and 13 */}
        <div style={{ ...card, display: 'grid', gap: 12 }} onPaste={iv.onPaste('residue')} {...iv.dropProps('residue')}>
          <SectionHead heading={t('will_sec_residue')} section="residue" st={iv.stateOf('residue')} onFile={f => iv.handleFile('residue', f)} />
          <Txt label={t('will_main_benef')} value={data.mainBeneficiaries || ''} onChange={v => set({ mainBeneficiaries: v })} ph={t('will_main_benef_ph')} />
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('will_second_tier')}</div>
          {second.map((h, i) => (
            <div key={i} onPaste={iv.onPaste(heirKey(2, i))} {...iv.dropProps(heirKey(2, i))}
              style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, display: 'grid', gap: 10, background: 'var(--bg-app)' }}>
              <HeirRowHead label={heirLabel(i + 1, 2)} st={iv.stateOf(heirKey(2, i))}
                onFile={f => iv.handleFile(heirKey(2, i), f)} onRemove={() => removeFrom('secondTier', i)} />
              <div style={{ display: 'grid', gridTemplateColumns: '84px 96px 1fr 1fr 1fr', gap: 10 }}>
                {/* The same rule, counted within the 2nd-tier list alone */}
                <Txt label={t('will_share')} value={i === 0 ? remainderShare(second) : h.share}
                  onChange={v => setList('secondTier', i, { share: v })} readOnly={i === 0} title={i === 0 ? t('will_share_auto') : undefined} />
                <Txt label={t('will_title')} value={h.title} onChange={v => setList('secondTier', i, { title: v })} />
                <Txt label={t('will_name')} value={h.name} onChange={v => setList('secondTier', i, { name: v })} />
                <Txt label={t('will_id_no')} value={h.idNo} onChange={v => setList('secondTier', i, { idNo: v })} />
                <Txt label={t('will_phone')} value={h.phone} onChange={v => setList('secondTier', i, { phone: v })} />
              </div>
              <AddrFields a={h.addr} onChange={(k, v) => setSecondAddr(i, k, v)} />
            </div>
          ))}
          <button type="button" onClick={() => addTo('secondTier', emptyHeir)} style={{ ...btnG, justifySelf: 'start' }}>+ {t('will_add_second')}</button>
          {second.length > 0 && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('will_share_auto')}</div>}
        </div>

        {/* Funeral wishes — clause 15 — and the signing */}
        <div style={{ ...card, display: 'grid', gap: 12 }} onPaste={iv.onPaste('funeral')} {...iv.dropProps('funeral')}>
          <SectionHead heading={t('will_sec_funeral')} section="funeral" st={iv.stateOf('funeral')} onFile={f => iv.handleFile('funeral', f)} />
          <Area label={t('will_funeral_wishes')} value={data.funeralWishes || ''} onChange={v => set({ funeralWishes: v })} ph={t('will_funeral_ph')} rows={3} />
          <div style={hdr}>{t('will_sec_signing')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Txt label={t('will_sign_date')} type="date" value={data.signDate || ''} onChange={v => set({ signDate: v })} />
            <Txt label={t('will_place')} value={data.signPlace || ''} onChange={v => set({ signPlace: v })} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('will_place_note')}</div>
        </div>

        {/* The two sign-off lines */}
        <div style={{ ...card, display: 'grid', gap: 12 }}>
          <div style={hdr}>{t('will_sec_signoff')}</div>
          <SignoffLines id={id} onChange={setApproved} />
        </div>

        {/* The will itself */}
        <div style={{ ...card, display: 'grid', gap: 12 }}>
          <WillDocument id={id} approved={approved} rev={rev} />
        </div>
      </div>
    </div>
  );
}

// ── The list ─────────────────────────────────────────────────────────────────────────────────────
function WillList({ onOpen, onNew }: { onOpen: (id: string) => void; onNew: () => void }) {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const d: any = await api.willList(q, status, page, limit); setRows(d.rows || []); setTotal(Number(d.total || 0)); }
    catch { /* the empty list says it */ }
    setLoading(false);
  };
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [q, status, page, limit]); // eslint-disable-line
  const del = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!window.confirm(t('will_delete_confirm'))) return;
    try { await api.willDelete(id); setRows(prev => prev.filter(r => r.id !== id)); } catch { /* */ }
  };
  const stateOf = (r: any) => r.rejected ? t('will_state_changes') : r.signedOff ? t('will_state_signed') : t('will_state_awaiting');
  const stateColor = (r: any) => r.rejected ? '#B42318' : r.signedOff ? '#16a34a' : 'var(--text-tertiary)';
  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
        <div style={{ width: '100%', maxWidth: PROP_LIST_WIDTH, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <input style={{ ...inp, flex: 1, minWidth: 120 }} placeholder={t('will_search')} value={q} onChange={e => { setPage(1); setQ(e.target.value); }} />
            <div style={{ display: 'flex', gap: 6, flexShrink: 0, overflowX: 'auto' }}>
              {STATUSES.map(s => (
                <button key={s || 'all'} onClick={() => { setPage(1); setStatus(s); }}
                  style={{ ...btnG, whiteSpace: 'nowrap', background: status === s ? 'var(--accent)' : 'var(--bg-surface)', color: status === s ? '#fff' : 'var(--text-secondary)', borderColor: status === s ? 'var(--accent)' : 'var(--border)' }}>
                  {s === 'new' ? t('will_st_new') : s === 'pending' ? t('will_st_pending') : s === 'confirmed' ? t('will_st_confirmed') : t('will_all')}
                </button>
              ))}
            </div>
          </div>
          {/* ONE bubble per will, where the list was a single bordered box of rows parted by thin grey
              lines (Calem, report 19597a46, 3rd opening: "Bubble everything in the menu below (except
              Lease Registration), separate the bubbles, and give them all a green border and a green ID
              number"). Due Diligence's and Legal Matters' card exactly — white, a green edge, 10px
              corners, 10px of page between them, the palest green under the pointer (propCard,
              utils/propertyCard.ts). The will number was already the green one, and the loading and
              "no wills" lines wear the card too. Everything on the row keeps its place. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {loading ? <div style={{ ...propCard, padding: 36, textAlign: 'center', color: 'var(--text-tertiary)' }}>{t('will_loading')}</div>
              : rows.length === 0 ? <div style={{ ...propCard, padding: 36, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('will_none')}</div>
                : rows.map(r => (
                  <div key={r.id} onClick={() => onOpen(r.id)} style={{ ...propCard, display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = PROP_CARD_HOVER)} onMouseLeave={e => (e.currentTarget.style.background = PROP_CARD_BG)}>
                    <span style={{ color: 'var(--accent)', fontWeight: 700, minWidth: 104 }}>{r.report_no}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{r.testator_name || t('will_untitled')}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {r.sign_date ? `${t('will_sign_date')}: ${r.sign_date}` : t('will_no_date')}
                        {r.updated_at ? ` · ${t('will_updated')} ${fmtDateTime(r.updated_at)}` : ''}
                        {r.updated_by_name ? ` · ${r.updated_by_name}` : ''}
                        {r.edited ? ` · ${t('will_edited')}` : ''}
                      </div>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: stateColor(r) }}>{stateOf(r)}</span>
                    {me?.role === 'admin' && (
                      <button title={t('will_delete')} onClick={e => del(e, r.id)} style={{ ...btnG, padding: '4px 9px', fontSize: 14, color: '#b91c1c', flexShrink: 0 }}>
                        <i className="ti ti-trash" aria-hidden />
                      </button>
                    )}
                  </div>
                ))}
          </div>
        </div>
      </div>
      <Pagination page={page} total={total} limit={limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />
      <Fab onClick={onNew} title={t('will_new')} />
    </div>
  );
}

export default function LastWill({ embedded }: { embedded?: boolean }) {
  useTranslation();
  const [openId, setOpenId] = useState('');
  // A deep link opens one will straight away: /m/slf_property?v=last_will&open=<id>, the same shape
  // the sign-off notices send.
  useEffect(() => {
    try {
      const v = new URLSearchParams(window.location.search).get('open');
      if (v) setOpenId(v);
    } catch { /* no query */ }
  }, []);
  const create = async () => { try { const r: any = await api.willCreate(); setOpenId(r.id); } catch { /* */ } };
  return (
    <div className="last-will" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg-app)', height: embedded ? undefined : '100vh', fontFamily: RC }}>
      {openId
        ? <WillForm id={openId} onBack={() => setOpenId('')} />
        : <WillList onOpen={setOpenId} onNew={create} />}
    </div>
  );
}
